/**
 * Chart primitives.
 *
 * Inline SVG rather than a charting library: nothing in this app ships one (d3
 * is an unused dependency), and these forms need less code than the wiring to
 * adopt one — with no bundle cost and no fight over how a library's defaults
 * look against our tokens.
 *
 * The rules every chart in here follows, so they read as one system:
 *
 *   ONE axis, always.        Two y-scales on one plot invent a relationship the
 *                            data does not have. Different units get two charts.
 *   Colour by identity only. Nominal categories (browsers, countries) are ONE
 *                            colour — bar length already encodes the value, and
 *                            shading it darker-where-bigger spends the identity
 *                            channel re-saying it. Slots come from
 *                            `chartPalette.categorical` in fixed order.
 *   The value is never       Every chart here pairs its marks with readable
 *   colour-only.             labels or a row of numbers, which is also what a
 *                            screen reader gets and what makes three of our
 *                            hues legal below 3:1 contrast.
 *   White does the           A 2px surface gap separates touching marks; a 2px
 *   separating.              surface ring keeps overlapping dots legible. Never
 *                            a stroke drawn round a mark — that is ink that is
 *                            not data.
 *   Chrome recedes.          Hairline solid gridlines one step off the surface.
 *                            Dashed reads as "threshold" when it is just a grid.
 *
 * Empty data is the caller's to handle: these render nothing rather than an
 * empty frame, because "no referrer data" is better said by a card's absence
 * than by an axis with nothing under it.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Box, Stack, Tooltip as MuiTooltip, Typography } from '@mui/material';
import { chartPalette, palette, radius, shadow, typography } from './designTokens';

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmt = (v) => num(v).toLocaleString();

// A percentage for display. gn2.me sends `pct` per row, computed on their side
// over the FULL set — preferred over a share of the rows on screen, which
// over-reads whenever their limit cut the tail.
const pctOf = (row, total) => (
  typeof row.pct === 'number' ? row.pct : (total > 0 ? (num(row.clicks) / total) * 100 : 0)
);

const pctText = (n) => `${n >= 10 ? Math.round(n) : Math.round(n * 10) / 10}%`;

/**
 * The chart's own pixel width, so its viewBox can match it 1:1.
 *
 * Without this a chart is drawn at a fixed viewBox width and stretched to fit,
 * which scales the TEXT and the STROKES along with the geometry: the same
 * component renders 6px axis labels in a half-width card and 3px-thick "2px"
 * lines in a full-width one. Measuring means 10.5px is 10.5px and 2px is 2px in
 * every card on the page.
 *
 * `fallback` is used until the first measurement lands, and forever in an
 * environment with no ResizeObserver (jsdom, so tests still render something
 * sensible rather than a zero-width plot).
 */
const useMeasuredWidth = (fallback) => {
  const ref = useRef(null);
  const [width, setWidth] = useState(fallback);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    let frame = 0;
    const measure = () => {
      frame = 0;
      const next = Math.round(el.clientWidth);
      // Bail out on no-op values so React skips the re-render entirely. A card
      // collapsed to zero (a closed Collapse) would otherwise divide the plot by
      // zero and render NaN coordinates, and re-rendering for a 0.4px change is
      // half of what makes the loop below.
      setWidth((prev) => (next > 0 && Math.abs(next - prev) >= 1 ? next : prev));
    };
    measure();

    // Resizing from inside the observer's own callback is what makes the
    // browser log "ResizeObserver loop completed with undelivered
    // notifications" — and CRA's dev overlay escalates that into a red screen.
    // The cycle here is real: the chart re-renders at a new height, the page
    // gets taller, the scrollbar appears, and every container loses ~15px.
    // Deferring the write to the next frame keeps the observation and the
    // mutation in separate delivery cycles. Same fix, same reason, as the mock
    // scaler in `module/capture/shared/SharePostModal.js`.
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    const observer = new ResizeObserver(schedule);
    observer.observe(el);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  return [ref, width];
};

/**
 * The top N categories with the tail folded into "Other".
 *
 * Not a display convenience — it is what keeps a donut legal. Past three
 * segments our hues stop being separable under deuteranopia (see
 * `chartPalette`), and a generated fourth hue would be indistinguishable from
 * one already on screen rather than a solution.
 */
export function foldTail(rows, limit) {
  const list = (Array.isArray(rows) ? rows : []).filter((r) => num(r.clicks) > 0);
  if (list.length <= limit) return list.map((r, i) => ({ ...r, color: chartPalette.categorical[i] }));

  const head = list.slice(0, limit).map((r, i) => ({ ...r, color: chartPalette.categorical[i] }));
  const tail = list.slice(limit);
  return [...head, {
    name: `Other (${tail.length})`,
    clicks: tail.reduce((n, r) => n + num(r.clicks), 0),
    uniqueVisitors: tail.reduce((n, r) => n + num(r.uniqueVisitors), 0),
    color: chartPalette.other,
    isOther: true,
  }];
}

/**
 * A floating value readout.
 *
 * Anchored to the card, not the cursor: a tooltip that chases the pointer is
 * hard to read and can leave the card. Tooltips ENHANCE here — they never gate
 * a value, because every chart also carries labels or a numbers row.
 */
// Pointer position inside a chart's container, for a tooltip that follows the
// mark it describes instead of sitting in a corner.
//
// Read off the DOM event rather than derived from a chart's own scales. The
// charts here place marks in viewBox units and sit at different offsets inside
// their container — TrendChart carries a legend above its SVG, the others do
// not — so one bounding-rect measurement is both simpler and correct for all
// of them. Attach `track` to the SAME element the tooltip is positioned
// against, and the numbers need no further translation.
const usePointer = () => {
  const [at, setAt] = useState(null);
  const latest = useRef(null);
  const frame = useRef(0);

  useEffect(() => () => {
    if (frame.current) cancelAnimationFrame(frame.current);
  }, []);

  // Coalesced to one state update per frame. `mousemove` fires far more often
  // than the screen repaints, and the choropleth re-renders ~200 country paths
  // per update — pushing every event straight into state makes dragging across
  // the map stutter. The newest reading is kept in a ref and the frame applies
  // it, so the tooltip lands where the pointer actually IS rather than where it
  // was when the frame was booked. Same technique, same reason, as the resize
  // observer above.
  const track = (event) => {
    const r = event.currentTarget.getBoundingClientRect();
    latest.current = {
      x: event.clientX - r.left,
      y: event.clientY - r.top,
      w: r.width,
      h: r.height,
    };
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      setAt(latest.current);
    });
  };

  const clear = () => {
    if (frame.current) {
      cancelAnimationFrame(frame.current);
      frame.current = 0;
    }
    setAt(null);
  };

  return [at, track, clear];
};

const TOOLTIP_GAP = 14;

// Anchored to whichever edge the pointer is NEAR, so the box always opens
// toward the empty half of the container. That is why this needs no measurement
// of the tooltip itself and cannot overflow whatever width it ends up at — the
// naive `left: x` would clip against the right edge on the last few days of a
// 30-day window, which is exactly where the newest data is.
//
// `at` absent means no pointer is being tracked: the corner-pinned placement is
// kept as the fallback rather than removed, so a caller that has no container
// to measure still gets a readable tooltip.
const placeTooltip = (at, align) => {
  if (!at) return { top: 0, [align]: 0 };
  return {
    ...(at.x < at.w / 2
      ? { left: at.x + TOOLTIP_GAP }
      : { right: (at.w - at.x) + TOOLTIP_GAP }),
    ...(at.y < at.h / 2
      ? { top: at.y + TOOLTIP_GAP }
      : { bottom: (at.h - at.y) + TOOLTIP_GAP }),
  };
};

const Tooltip = ({ title, rows, align = 'right', at = null }) => (
  <Box
    sx={{
      position: 'absolute', ...placeTooltip(at, align), zIndex: 2,
      bgcolor: palette.surface, border: `1px solid ${palette.line}`,
      borderRadius: `${radius.sm}px`, px: 1.25, py: 0.85, boxShadow: shadow.card,
      pointerEvents: 'none', minWidth: 108,
    }}
  >
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: palette.ink, mb: 0.35 }}>
      {title}
    </Typography>
    {rows.map((r) => (
      <Stack key={r.label} direction="row" alignItems="center" spacing={0.6}>
        {r.color && (
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: r.color, flexShrink: 0 }} />
        )}
        <Typography sx={{ fontSize: 11, color: palette.muted }}>
          {r.label}
        </Typography>
        <Typography
          sx={{
            fontSize: 11, fontWeight: 700, color: palette.ink, ml: 'auto',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {r.value}
        </Typography>
      </Stack>
    ))}
  </Box>
);

/**
 * The identity channel that does not depend on telling colours apart.
 *
 * Always present for two or more series. A single series gets none — there is
 * one colour, so the card's title already says what is plotted, and a box with
 * one swatch just restates it.
 */
export const ChartLegend = ({ items, sx }) => (
  <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={0.75} sx={sx}>
    {items.map((s) => (
      <Stack key={s.label} direction="row" alignItems="center" spacing={0.6}>
        <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: s.color }} />
        <Typography sx={{ fontSize: 12, color: palette.muted }}>{s.label}</Typography>
      </Stack>
    ))}
  </Stack>
);

/** A column with a 4px rounded cap, square where it meets the baseline. */
const cappedColumn = (x, y, w, h, r = 4) => {
  const cap = Math.min(r, w / 2, Math.max(0, h));
  return [
    `M${x},${y + h}`, `L${x},${y + cap}`, `Q${x},${y} ${x + cap},${y}`,
    `L${x + w - cap},${y}`, `Q${x + w},${y} ${x + w},${y + cap}`,
    `L${x + w},${y + h}`, 'Z',
  ].join(' ');
};

/** Clean axis ticks — 0, half, peak — rounded so they read as numbers. */
const ticksFor = (peak) => [0, 0.5, 1].map((t) => Math.round(peak * t))
  .filter((t, i, a) => a.indexOf(t) === i);

/**
 * Clicks and unique visitors over time, as a line or as columns.
 *
 * Both series count people, so they share one scale and one axis. The toggle is
 * a VIEW switch, not a filter — the window that scopes the whole screen lives
 * in one row above every card, never inside one.
 */
export const TrendChart = ({ rows, series, mode = 'line', height = 200 }) => {
  const [hover, setHover] = useState(null);
  const [at, trackPointer, clearPointer] = usePointer();
  const [ref, W] = useMeasuredWidth(720);
  if (!rows.length) return null;

  const H = height;
  const PAD = { top: 14, right: 14, bottom: 26, left: 40 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  // Over BOTH series, so the two are comparable by eye. `|| 1` keeps an
  // all-zero window from dividing by zero rather than from being drawn.
  const peak = Math.max(1, ...rows.flatMap((r) => series.map((s) => num(r[s.key]))));
  const band = plotW / rows.length;
  const x = (i) => (rows.length === 1
    ? PAD.left + plotW / 2
    : PAD.left + (i / (rows.length - 1)) * plotW);
  const y = (v) => PAD.top + plotH - (num(v) / peak) * plotH;

  // Columns share the band between the series, with a 2px surface gap between
  // neighbours so they read as separate without a stroke around them.
  const colW = Math.min(24, Math.max(3, (band - 6) / series.length - 2));

  return (
    <Box
      ref={ref}
      sx={{ position: 'relative' }}
      onMouseMove={trackPointer}
      onMouseLeave={clearPointer}
    >
      <ChartLegend items={series} sx={{ mb: 1.25 }} />

      <Box
        component="svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`${series.map((s) => s.label).join(' and ')} across ${rows.length} days`}
        sx={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}
        onMouseLeave={() => setHover(null)}
      >
        {ticksFor(peak).map((t) => (
          <g key={t}>
            <line
              x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)}
              stroke={chartPalette.grid} strokeWidth="1"
            />
            <text
              x={PAD.left - 8} y={y(t) + 3.5} textAnchor="end"
              fill={palette.hint} fontSize="10.5" style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {fmt(t)}
            </text>
          </g>
        ))}

        {mode === 'line' ? (
          <>
            {series.map((s) => (
              <polyline
                key={s.key}
                fill="none" stroke={s.color} strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round"
                points={rows.map((r, i) => `${x(i)},${y(r[s.key])}`).join(' ')}
              />
            ))}
            {/* One day has no line to read, so it is drawn as points. */}
            {rows.length === 1 && series.map((s) => (
              <circle
                key={s.key} cx={x(0)} cy={y(rows[0][s.key])} r="5"
                fill={s.color} stroke={palette.surface} strokeWidth="2"
              />
            ))}
          </>
        ) : (
          rows.map((r, i) => {
            const groupW = colW * series.length + 2 * (series.length - 1);
            const left = (rows.length === 1 ? x(0) : PAD.left + band * i + band / 2) - groupW / 2;
            return series.map((s, si) => {
              const h = plotH - (y(r[s.key]) - PAD.top);
              return (
                <path
                  key={s.key}
                  d={cappedColumn(left + si * (colW + 2), y(r[s.key]), colW, h)}
                  fill={s.color}
                  opacity={hover === null || hover === i ? 1 : 0.35}
                />
              );
            });
          })
        )}

        {hover !== null && mode === 'line' && (
          <>
            <line
              x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + plotH}
              stroke={palette.hint} strokeWidth="1"
            />
            {series.map((s) => (
              <circle
                key={s.key} cx={x(hover)} cy={y(rows[hover][s.key])} r="5"
                fill={s.color} stroke={palette.surface} strokeWidth="2"
              />
            ))}
          </>
        )}

        {/* Hit targets far wider than the marks — a 2px line is not a pointer
            target. One invisible column per day, full plot height. */}
        {rows.map((r, i) => (
          <rect
            key={r.name}
            x={rows.length === 1 ? PAD.left : PAD.left + band * i}
            y={PAD.top}
            width={rows.length === 1 ? plotW : band}
            height={plotH}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
          />
        ))}

        {/* First and last only: a label on every point is noise at 90 days. */}
        {[0, rows.length - 1].filter((i, n, a) => i >= 0 && a.indexOf(i) === n).map((i) => (
          <text
            key={i} x={x(i)} y={H - 8}
            textAnchor={rows.length === 1 ? 'middle' : (i === 0 ? 'start' : 'end')}
            fill={palette.hint} fontSize="10.5"
          >
            {rows[i].name}
          </text>
        ))}
      </Box>

      {hover !== null && (
        <Tooltip
          at={at}
          title={rows[hover].name}
          rows={series.map((s) => ({
            label: s.label, color: s.color, value: fmt(rows[hover][s.key]),
          }))}
        />
      )}
    </Box>
  );
};

/**
 * Part-to-whole, at a glance.
 *
 * At most three colours plus "Other" — see `chartPalette` for why that is a
 * hard limit and not a style choice. Every segment is direct-labelled beside
 * the ring: the two closest hues sit in the floor band for colour-vision
 * separation, and labels are what make that legal.
 *
 * A single category is NOT drawn as a full circle. "Desktop 100%" as a complete
 * ring is a chart with nothing to compare — the caller renders a stat tile.
 */
export const DonutChart = ({ rows, centerLabel }) => {
  const [hover, setHover] = useState(null);
  if (!rows.length) return null;

  const total = rows.reduce((n, r) => n + num(r.clicks), 0);
  if (total <= 0) return null;

  // Sized for the card it actually lives in, which is half of a column and not
  // the page. At 168/26 the ring took nearly every pixel of a two-up card and
  // left the labels beside it about forty — which is where "Chrome" became
  // "Chr…". The ring is a proportion; it does not read any better large.
  const SIZE = 132;
  const STROKE = 21;
  const R = (SIZE - STROKE) / 2 - 2;
  const C = 2 * Math.PI * R;
  // The 2px surface gap, expressed as arc length. Segments smaller than the gap
  // would render inside-out, so the gap is dropped for those.
  const GAP = 2;

  let offset = 0;
  const arcs = rows.map((r) => {
    const frac = num(r.clicks) / total;
    const len = frac * C;
    // The gap separates a segment from its NEIGHBOUR. A single category has
    // none, so a gap there is just a nick taken out of a complete ring.
    const gap = rows.length > 1 && len > GAP * 2 ? GAP : 0;
    const arc = { ...r, len: Math.max(0, len - gap), offset, frac };
    offset += len;
    return arc;
  });

  const focus = hover !== null ? rows[hover] : null;

  return (
    // Wrapping, not a breakpoint: these cards sit two to a row inside a column,
    // so the space this chart has is nothing the viewport knows about — at
    // 1400px wide the card can still be 300px. The legend claims 150px and
    // drops under the ring when it cannot have them.
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        // Cards in a row all end at the height of the tallest, so a chart that
        // only fills half of one has to say where the other half goes. Centred:
        // a ring pinned to the top of an otherwise empty card looks like
        // something failed to load underneath it.
        alignContent: 'center',
        height: '100%',
        gap: 2,
      }}
    >
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <Box
          component="svg"
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          role="img"
          aria-label={rows.map((r) => `${r.name} ${fmt(r.clicks)}`).join(', ')}
          sx={{ width: SIZE, height: SIZE, display: 'block', transform: 'rotate(-90deg)' }}
          onMouseLeave={() => setHover(null)}
        >
          {arcs.map((a, i) => (
            <circle
              key={a.name}
              cx={SIZE / 2} cy={SIZE / 2} r={R}
              fill="none"
              stroke={a.color}
              strokeWidth={hover === i ? STROKE + 4 : STROKE}
              strokeDasharray={`${a.len} ${C - a.len}`}
              strokeDashoffset={-a.offset}
              opacity={hover === null || hover === i ? 1 : 0.4}
              style={{ transition: 'stroke-width 140ms ease, opacity 140ms ease', cursor: 'pointer' }}
              onMouseEnter={() => setHover(i)}
            />
          ))}
        </Box>

        {/* The middle of a donut is free space the ring already paid for. */}
        <Stack
          alignItems="center" justifyContent="center"
          sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
        >
          <Typography sx={{ fontSize: 19, fontWeight: 800, color: palette.ink, lineHeight: 1.1 }}>
            {focus ? pctText(pctOf(focus, total)) : fmt(total)}
          </Typography>
          <Typography sx={{ ...typography.micro, fontSize: 10 }}>
            {focus ? focus.name : (centerLabel || 'clicks')}
          </Typography>
        </Stack>
      </Box>

      {/* Direct labels. Not decoration — they are the relief channel that makes
          the two closest hues legal, and they carry the exact value the ring
          can only approximate. */}
      <Stack spacing={0.35} sx={{ minWidth: 0, flex: '1 1 150px' }}>
        {rows.map((r, i) => (
          <Stack
            key={r.name}
            direction="row" alignItems="center" spacing={1}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
            sx={{
              // The same row shape the ranked lists use, so a donut card and a
              // list card read as the same object at a glance. Hovering lifts
              // the row and its arc together.
              px: 1, py: 0.7, borderRadius: `${radius.sm}px`,
              cursor: 'default',
              bgcolor: hover === i ? palette.surfaceAlt : 'transparent',
              opacity: hover === null || hover === i ? 1 : 0.5,
              transition: 'background-color 160ms cubic-bezier(0.4, 0, 0.2, 1), opacity 160ms',
            }}
          >
            <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: r.color, flexShrink: 0 }} />
            {/* These labels are the one thing on the card that can be cut —
                "Chrome Mobile WebView" has nowhere to go beside a ring — and a
                row reading "Chr…" is worse than no row. The browser's own
                `title` was already here and takes a second to appear, in a
                system font, at the pointer; this is the tooltip the rest of the
                app uses. */}
            <MuiTooltip arrow enterDelay={300} title={r.name}>
              <Typography
                sx={{
                  fontSize: 12.5, color: palette.ink, minWidth: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
              >
                {r.name}
              </Typography>
            </MuiTooltip>
            <Typography
              sx={{
                fontSize: 12.5, fontWeight: 700, color: palette.ink, ml: 'auto',
                fontVariantNumeric: 'tabular-nums', flexShrink: 0,
              }}
            >
              {fmt(r.clicks)}
            </Typography>
            <Typography
              sx={{
                fontSize: 11, color: palette.muted, flexShrink: 0,
                fontVariantNumeric: 'tabular-nums', width: 42, textAlign: 'right',
              }}
            >
              {pctText(pctOf(r, total))}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

/**
 * Magnitude across an ordered scale — hours of the day.
 *
 * ONE hue, because the categories have a natural order and the question is
 * "when", not "which". Colouring each hour differently would spend the identity
 * channel on something the position already says.
 */
export const ColumnChart = ({ rows, height = 170, valueLabel = 'Clicks' }) => {
  const [hover, setHover] = useState(null);
  const [at, trackPointer, clearPointer] = usePointer();
  const [ref, W] = useMeasuredWidth(520);
  if (!rows.length) return null;

  const H = height;
  const PAD = { top: 14, right: 8, bottom: 24, left: 34 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  const peak = Math.max(1, ...rows.map((r) => num(r.clicks)));
  const band = plotW / rows.length;
  const colW = Math.min(24, Math.max(3, band - 2)); // the 2px surface gap
  const y = (v) => PAD.top + plotH - (num(v) / peak) * plotH;

  // A label per hour collides at 24 columns. Thin to roughly six.
  const step = Math.max(1, Math.ceil(rows.length / 6));

  return (
    <Box
      ref={ref}
      sx={{ position: 'relative' }}
      onMouseMove={trackPointer}
      onMouseLeave={clearPointer}
    >
      <Box
        component="svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`${valueLabel} across ${rows.length} buckets`}
        sx={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}
        onMouseLeave={() => setHover(null)}
      >
        {ticksFor(peak).map((t) => (
          <g key={t}>
            <line
              x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)}
              stroke={chartPalette.grid} strokeWidth="1"
            />
            <text
              x={PAD.left - 8} y={y(t) + 3.5} textAnchor="end"
              fill={palette.hint} fontSize="10.5" style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {fmt(t)}
            </text>
          </g>
        ))}

        {rows.map((r, i) => {
          const h = plotH - (y(r.clicks) - PAD.top);
          const left = PAD.left + band * i + (band - colW) / 2;
          return (
            <g key={r.name}>
              <path
                d={cappedColumn(left, y(r.clicks), colW, h)}
                fill={chartPalette.categorical[0]}
                opacity={hover === null || hover === i ? 1 : 0.35}
              />
              {/* Hit target spans the whole band, so a 3px column on a busy
                  day is still reachable. */}
              <rect
                x={PAD.left + band * i} y={PAD.top} width={band} height={plotH}
                fill="transparent" onMouseEnter={() => setHover(i)}
              />
            </g>
          );
        })}

        {rows.map((r, i) => (i % step === 0 ? (
          <text
            key={r.name}
            x={PAD.left + band * i + band / 2} y={H - 7}
            textAnchor="middle" fill={palette.hint} fontSize="10.5"
          >
            {r.name}
          </text>
        ) : null))}
      </Box>

      {hover !== null && (
        <Tooltip
          at={at}
          title={rows[hover].name}
          rows={[{
            label: valueLabel,
            color: chartPalette.categorical[0],
            value: fmt(rows[hover].clicks),
          }]}
        />
      )}
    </Box>
  );
};

/**
 * A ranked list of nominal categories — browsers, countries, referrers.
 *
 * The magnitude is the ROW, not a bar under it. An earlier version drew a 6px
 * slab of full-strength brand red beneath each label, and it failed twice: at
 * full width it read as a rule ruling off the label rather than as data, and a
 * single 100% category — which is most of these dimensions on a young account —
 * was a solid line across the card saying nothing at all. A tinted fill behind
 * the values encodes the same share, reads as one object per row, and cannot be
 * mistaken for a divider.
 *
 * ONE colour for every row. These categories have no natural order, so length
 * is the whole encoding; shading them darker-where-bigger would re-say it in
 * the one channel left for identity.
 *
 * This is simultaneously the chart AND its table view: the fill answers "which
 * is biggest" at a glance, and the numbers on top are the same data read
 * exactly — which is also what a screen reader gets, hence the explicit roles.
 */
export const RankedBars = ({ rows, showUnique = true, max = 8 }) => {
  const list = rows.slice(0, max);
  if (!list.length) return null;

  const total = list.reduce((n, r) => n + num(r.clicks), 0);
  const col = { width: 54, textAlign: 'right', flexShrink: 0 };

  return (
    <Box role="table">
      <Stack
        role="row"
        direction="row" alignItems="center" spacing={1}
        sx={{ px: 1, pb: 0.6 }}
      >
        <Typography role="columnheader" sx={{ ...typography.micro, flex: 1, minWidth: 0 }}>
          Value
        </Typography>
        <Typography role="columnheader" sx={{ ...typography.micro, ...col }}>Clicks</Typography>
        {showUnique && (
          <Typography role="columnheader" sx={{ ...typography.micro, ...col }}>Unique</Typography>
        )}
      </Stack>

      <Stack spacing={0.35}>
        {list.map((row) => {
          const share = Math.min(100, pctOf(row, total));
          // Hard colour stops, not a fade: the EDGE of the fill is the value,
          // and a soft gradient would blur exactly the thing being read. The
          // second pair paints the remainder as a faint track, so the row is
          // always a whole with a part filled in.
          const fill = (c) => 'linear-gradient(90deg, '
            + `${c} 0%, ${c} ${share}%, `
            + `${chartPalette.rowTrack} ${share}%, ${chartPalette.rowTrack} 100%)`;
          return (
            <Stack
              role="row"
              key={row.name}
              direction="row" alignItems="center" spacing={1}
              sx={{
                px: 1, py: 0.85,
                borderRadius: `${radius.sm}px`,
                backgroundImage: fill(chartPalette.rowFill),
                transition: 'background-image 160ms cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': { backgroundImage: fill(chartPalette.rowFillHover) },
              }}
            >
              <Stack
                role="cell"
                direction="row" alignItems="baseline" spacing={0.75}
                sx={{ flex: 1, minWidth: 0 }}
              >
                <MuiTooltip arrow enterDelay={300} title={row.name}>
                  <Typography
                    sx={{
                      fontSize: 12.5, color: palette.ink, minWidth: 0,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {row.name}
                  </Typography>
                </MuiTooltip>
                <Typography sx={{ fontSize: 11, color: palette.muted, flexShrink: 0 }}>
                  {pctText(share)}
                </Typography>
              </Stack>
              <Typography
                role="cell"
                sx={{
                  ...col, fontSize: 12.5, fontWeight: 700, color: palette.ink,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fmt(row.clicks)}
              </Typography>
              {showUnique && (
                <Typography
                  role="cell"
                  sx={{
                    ...col, fontSize: 12.5, color: palette.muted,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {fmt(row.uniqueVisitors)}
                </Typography>
              )}
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};

/**
 * A world choropleth — clicks by country.
 *
 * No mapping library and no projection dependency. The geometry is plain
 * lon/lat rings, and the projection is equirectangular, which is four lines of
 * arithmetic: every degree of longitude is the same width, every degree of
 * latitude the same height. That is the flat rectangular world people already
 * read on dashboards, and adopting `d3-geo` to draw it would cost a dependency
 * to produce the same picture.
 *
 * Latitude is cropped to 84°N–58°S. The full range spends a third of the card's
 * height on Antarctica and the empty Southern Ocean, which pushes every
 * populated landmass into the top half.
 *
 * SEQUENTIAL colour, not categorical: the question is "how much", so it is one
 * hue getting darker, and countries keep their meaning without a legend lookup.
 * "No data" is a neutral OUTSIDE the ramp — a pale tint of it would say "least
 * traffic", which is a different and false claim.
 *
 * The geometry is loaded on demand. It is ~58KB, and a creator who never opens
 * the map should never download it — which is also why this cannot be a static
 * import at the top of the file. Webpack splits it into its own chunk; if that
 * import ever becomes static, every page that touches this module gains 58KB.
 *
 * Where `worldCountries.json` came from
 * ------------------------------------
 * Natural Earth 1:110m Admin 0 Countries, which is public domain — no
 * permission or attribution required (naturalearthdata.com/about/terms-of-use).
 * Reduced from 838KB to 58KB: every property except the ISO-2 code and the name
 * dropped, coordinates rounded to 2dp, Douglas–Peucker simplified at 0.35°
 * (sub-pixel at the size this renders), and rings whose bounding box is under
 * 0.8° removed — micro-islands that cost bytes to draw one speck.
 *
 * The shape is `[{ c: ISO2, n: name, r: [[lon, lat, lon, lat, …], …] }]` —
 * flat coordinate arrays rather than nested pairs, which is most of the saving.
 * Two entries have no ISO-2 code because none exists (N. Cyprus, Somaliland);
 * they render as "no data", which is the honest answer. Natural Earth labels
 * Taiwan `CN-TW`; it is stored as `TW` here, because that is what gn2.me
 * sends and a code that never matches is a country that never shades.
 */
export const ChoroplethMap = ({ rows, height, onCountryHover }) => {
  const [geo, setGeo] = useState(null);
  const [failed, setFailed] = useState(false);
  const [hover, setHover] = useState(null);
  const [at, trackPointer, clearPointer] = usePointer();
  const [ref, W] = useMeasuredWidth(520);

  useEffect(() => {
    let gone = false;
    import('./worldCountries.json')
      .then((mod) => { if (!gone) setGeo(mod.default || mod); })
      .catch(() => { if (!gone) setFailed(true); });
    return () => { gone = true; };
  }, []);

  // gn2.me keys country rows by ISO-2 in `code`. Falling back to the name makes
  // the map survive a row that arrives without one.
  const byCode = new Map();
  const byName = new Map();
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    if (r.code) byCode.set(String(r.code).toUpperCase(), r);
    if (r.name) byName.set(String(r.name).toLowerCase(), r);
  });
  const valueFor = (country) => (
    byCode.get(country.c) || byName.get(String(country.n).toLowerCase()) || null
  );

  const peak = Math.max(1, ...(Array.isArray(rows) ? rows : []).map((r) => num(r.clicks)));
  const steps = chartPalette.sequential;
  // Buckets on a square-root scale. Click counts are heavily skewed — one home
  // country and a long tail of single visits — and a linear scale puts that
  // whole tail in the palest step, which is the one that reads as "almost
  // nothing". The root spreads the low end where the countries actually are.
  const fillFor = (clicks) => {
    if (!clicks) return chartPalette.noData;
    const t = Math.sqrt(num(clicks) / peak);
    return steps[Math.min(steps.length - 1, Math.max(0, Math.ceil(t * steps.length) - 1))];
  };

  if (failed) {
    return (
      <Typography sx={{ fontSize: 12.5, color: palette.muted }}>
        The map could not be loaded. The list view has the same numbers.
      </Typography>
    );
  }

  const LAT_N = 84;
  const LAT_S = -58;
  // The projection's own aspect, not a fixed 300px. `px` and `py` scale to W
  // and H independently, so a height that does not follow the width stretches
  // every country to fill it — at 300 wide and 300 tall the world was drawn
  // two and a half times too tall, which is what turned India into a splinter
  // and left a band of empty ocean above and below it. 360° of longitude over
  // 142° of latitude is the ratio; a caller can still override it.
  const H = height || Math.round((W * (LAT_N - LAT_S)) / 360);
  const px = (lon) => ((num(lon) + 180) / 360) * W;
  const py = (lat) => ((LAT_N - num(lat)) / (LAT_N - LAT_S)) * H;

  // One path per country: every ring in one `d`, with evenodd so an inner ring
  // punches a hole (Lesotho, the Vatican) instead of painting over it.
  const pathFor = (country) => country.r.map((flat) => {
    let d = '';
    for (let i = 0; i < flat.length; i += 2) {
      d += `${i === 0 ? 'M' : 'L'}${px(flat[i]).toFixed(1)},${py(flat[i + 1]).toFixed(1)}`;
    }
    return `${d}Z`;
  }).join('');

  const focus = hover !== null && geo ? geo[hover] : null;
  const focusRow = focus ? valueFor(focus) : null;

  return (
    <Box
      ref={ref}
      sx={{ position: 'relative' }}
      onMouseMove={trackPointer}
      onMouseLeave={clearPointer}
    >
      <Box
        component="svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Clicks by country across ${byCode.size || byName.size} countries`}
        sx={{
          width: '100%', height: 'auto', display: 'block',
          overflow: 'hidden', borderRadius: `${radius.sm}px`,
          // A ground under the continents. Floating on the card's white, the
          // land had no edge to be inside of and the whole thing read as a
          // stain rather than a map.
          bgcolor: palette.surfaceAlt,
        }}
        onMouseLeave={() => { setHover(null); if (onCountryHover) onCountryHover(null); }}
      >
        {!geo ? (
          <text
            x={W / 2} y={H / 2} textAnchor="middle"
            fill={palette.hint} fontSize="12"
          >
            Loading map…
          </text>
        ) : geo.map((country, i) => {
          const row = valueFor(country);
          return (
            <path
              key={`${country.c || country.n}`}
              d={pathFor(country)}
              fill={fillFor(row && row.clicks)}
              fillRule="evenodd"
              // A hairline in the SURFACE colour, not a darker outline: it is
              // the 2px-gap rule at map scale, separating neighbours without
              // adding ink that reads as data.
              stroke={palette.surface}
              strokeWidth={hover === i ? 1.4 : 0.6}
              style={{ cursor: row ? 'pointer' : 'default' }}
              onMouseEnter={() => {
                setHover(i);
                if (onCountryHover) onCountryHover(row);
              }}
            />
          );
        })}
      </Box>

      {focus && (
        <Tooltip
          at={at}
          title={(focusRow && focusRow.name) || focus.n}
          align="left"
          rows={[{
            label: 'Clicks',
            color: fillFor(focusRow && focusRow.clicks),
            value: focusRow ? fmt(focusRow.clicks) : 'No data',
          }]}
        />
      )}

      {/* The scale, spelled out. A choropleth encodes its value in colour
          alone, so the ramp has to be readable as a ramp — and the list view
          beside it carries the exact numbers. */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1 }} flexWrap="wrap" rowGap={0.75}>
        <Stack direction="row" alignItems="center" spacing={0.6}>
          <Typography sx={{ fontSize: 11, color: palette.hint }}>Low</Typography>
          <Stack direction="row" spacing="2px">
            {steps.map((c) => (
              <Box key={c} sx={{ width: 16, height: 9, bgcolor: c, borderRadius: '2px' }} />
            ))}
          </Stack>
          <Typography sx={{ fontSize: 11, color: palette.hint }}>High</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.6}>
          <Box
            sx={{
              width: 16, height: 9, bgcolor: chartPalette.noData,
              borderRadius: '2px', border: `1px solid ${palette.line}`,
            }}
          />
          <Typography sx={{ fontSize: 11, color: palette.hint }}>No data</Typography>
        </Stack>
      </Stack>
    </Box>
  );
};
